import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, XCircle } from "lucide-react";
import api from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";

export function PaymentSuccess() {
  const loc = useLocation();
  const sessionId = new URLSearchParams(loc.search).get("session_id");
  const [status, setStatus] = useState("polling");
  const [details, setDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const { clear } = useCart();
  const { refresh } = useAuth();

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        setDetails(r.data);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          clear();
          refresh();
          return;
        }
        if (["failed", "expired"].includes(r.data.payment_status)) {
          setStatus("failed");
          return;
        }
      } catch (err) {
        console.warn("Payment status poll failed:", err?.message);
      }
      setAttempts((a) => a + 1);
    };
    poll();
    const iv = setInterval(poll, 2500);
    return () => { cancelled = true; clearInterval(iv); };
  }, [sessionId, clear, refresh]);

  useEffect(() => {
    if (attempts > 20 && status === "polling") setStatus("timeout");
  }, [attempts, status]);

  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-success-page">
      {status === "polling" && <>
        <div className="animate-pulse text-6xl">⧗</div>
        <h1 className="font-display text-3xl mt-4">Confirming payment…</h1>
        <p className="text-zinc-400 mt-2">Hang tight — this only takes a moment.</p>
      </>}
      {status === "paid" && <>
        <CheckCircle2 className="mx-auto text-[var(--cl-orange)]" size={64} />
        <h1 className="font-display text-4xl mt-4">You're In.</h1>
        <p className="text-zinc-300 mt-3">{details?.type === "subscription" ? "Membership activated. Welcome to the club." : "Order confirmed — packing soon."}</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/account" className="btn-primary" data-testid="success-account">Go to Account</Link>
          <Link to="/shop" className="btn-outline" data-testid="success-shop">Keep Shopping</Link>
        </div>
      </>}
      {status === "failed" && <>
        <XCircle className="mx-auto text-red-500" size={64} />
        <h1 className="font-display text-3xl mt-4">Payment Failed</h1>
        <Link to="/cart" className="btn-primary inline-block mt-6">Return to Cart</Link>
      </>}
      {status === "timeout" && <>
        <h1 className="font-display text-3xl mt-4">Still processing…</h1>
        <p className="text-zinc-400 mt-2">We'll email you a confirmation shortly. Check your account for order status.</p>
        <Link to="/account" className="btn-primary inline-block mt-6">Go to Account</Link>
      </>}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-lg mx-auto px-6 py-24 text-center" data-testid="payment-cancel-page">
      <XCircle className="mx-auto text-zinc-500" size={64} />
      <h1 className="font-display text-3xl mt-4">Checkout Cancelled</h1>
      <p className="text-zinc-400 mt-2">No worries — your cart is saved.</p>
      <Link to="/cart" className="btn-primary inline-block mt-6" data-testid="cancel-back-cart">Back to Cart</Link>
    </div>
  );
}
